import { useState } from 'react'

import { t } from '../../i18n'
import { cityName, timeIn, utcOffset, utcOffsetMinutes } from '../../lib/format'
import { Button, Empty } from '../Card'
import { SheetShell } from '../Sheet'
import { CheckMark, Chevron } from './CheckMark'
import { SearchField } from './SearchField'

export function TimezonePicker({
  value,
  system,
  zones,
  onChange,
}: {
  value: string
  system: string
  zones: string[]
  onChange: (zone: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState(value)

  const matching = zones.filter((zone) => matches(zone, query))
  const groups = group(matching.filter((zone) => zone !== system))
  const systemShown = matches(system, query)

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setQuery('')
          setDraft(value)
          setOpen(true)
        }}
        aria-label={t.settings.timezoneTitle}
        className="pressable flex w-full items-center gap-[10px] text-left"
      >
        <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-chip bg-tint text-accent">
          <GlobeIcon />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-row">{cityName(value)}</span>
          <span className="tabular mt-px block text-hint text-text-muted">
            {utcOffset(value)}, {timeIn(value)}
          </span>
        </span>
        <span
          className={`flex-none rounded-chip px-2 py-[6px] text-note font-medium ${
            value === system ? 'bg-fill text-text-secondary' : 'bg-tint text-accent'
          }`}
        >
          {value === system ? t.settings.timezoneSystem : t.settings.timezoneManual}
        </span>
        <span className="flex-none text-text-secondary">
          <Chevron />
        </span>
      </button>

      {open ? (
        <SheetShell
          matte
          title={t.settings.timezoneTitle}
          onClose={() => setOpen(false)}
          footer={
            <Button
              onClick={() => {
                onChange(draft)
                setOpen(false)
              }}
            >
              {t.common.done}
            </Button>
          }
        >
          <div className="pb-2 pt-[10px]">
            <SearchField
              autoFocus
              compact
              value={query}
              onChange={setQuery}
              placeholder={t.settings.timezoneSearch}
              count={matching.length}
              clearLabel={t.common.clear}
            />
          </div>

          <div className="pb-[10px]">
            {systemShown ? (
              <>
                <Heading first>{t.settings.timezoneFromSystem}</Heading>
                <Row zone={system} selected={system === draft} onSelect={() => setDraft(system)} />
              </>
            ) : null}

            {groups.map(([region, list]) => (
              <div key={region}>
                <Heading>{t.settings.regions[region] ?? region}</Heading>
                <div className="flex flex-col gap-[5px]">
                  {list.map((zone) => (
                    <Row
                      key={zone}
                      zone={zone}
                      selected={zone === draft}
                      onSelect={() => setDraft(zone)}
                    />
                  ))}
                </div>
              </div>
            ))}

            {matching.length === 0 ? (
              <Empty title={t.common.nothingFound} hint={t.settings.timezoneEmpty} />
            ) : null}
          </div>
        </SheetShell>
      ) : null}
    </>
  )
}

function GlobeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="10" cy="10" r="7.4" strokeWidth="1.5" />
      <path
        d="M2.8 10h14.4M10 2.6c1.9 2 2.9 4.6 2.9 7.4s-1 5.4-2.9 7.4c-1.9-2-2.9-4.6-2.9-7.4s1-5.4 2.9-7.4z"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function Heading({ children, first }: { children: string; first?: boolean }) {
  return (
    <div
      className={`sticky top-0 z-1 bg-sheet-matte px-[2px] pb-[6px] text-label uppercase text-text-muted ${
        first ? 'pt-[6px]' : 'pt-3'
      }`}
    >
      {children}
    </div>
  )
}

function Row({
  zone,
  selected,
  onSelect,
}: {
  zone: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`pressable flex min-h-field-row w-full items-center gap-[10px] rounded-row border px-3 text-left transition-colors duration-row ${
        selected ? 'border-accent bg-tint' : 'border-hairline bg-field'
      }`}
    >
      <span className="tabular w-[70px] flex-none whitespace-nowrap text-offset text-text-strong">
        {utcOffset(zone)}
      </span>
      <span className="min-w-0 flex-1 truncate text-list">{cityName(zone)}</span>
      <span className="tabular flex-none text-meta text-text-secondary">{timeIn(zone)}</span>
      <span className="flex w-4 flex-none justify-center text-accent">
        {selected ? (
          <CheckMark size={14} />
        ) : (
          <span className="h-[14px] w-[14px] rounded-full border border-hairline" />
        )}
      </span>
    </button>
  )
}

function matches(zone: string, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return `${utcOffset(zone)} ${cityName(zone)} ${zone.replace(/[/_]/g, ' ')}`
    .toLowerCase()
    .includes(trimmed)
}

function group(zones: string[]): [string, string[]][] {
  const groups = new Map<string, string[]>()
  for (const zone of zones) {
    const region = zone.includes('/') ? (zone.split('/')[0] ?? zone) : zone
    groups.set(region, [...(groups.get(region) ?? []), zone])
  }
  for (const [region, list] of groups) {
    groups.set(
      region,
      list.toSorted(
        (left, right) =>
          utcOffsetMinutes(left) - utcOffsetMinutes(right) ||
          cityName(left).localeCompare(cityName(right), 'en'),
      ),
    )
  }
  return [...groups.entries()]
}
